import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, MapPin, X, Building2, Search } from "lucide-react";
import { ScoreBadge, EmptyState } from "./Dashboard";

export default function PropertiesPage() {
  const [props, setProps] = useState([]);
  const [groups, setGroups] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = () => api.get("/properties").then((r) => setProps(r.data)).finally(() => setLoading(false));

  useEffect(() => {
    reload();
    api.get("/jurisdictions").then((r) => setGroups(r.data));
  }, []);

  return (
    <div className="space-y-6 animate-slide-up" data-testid="properties-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Portfolio</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 font-display">Properties</h1>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="open-add-property" className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-lg text-sm hover:bg-slate-800 transition">
          <Plus className="w-4 h-4" /> Add property
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : props.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12">
          <div className="flex flex-col items-center text-center">
            <Building2 className="w-10 h-10 text-slate-400 mb-3" />
            <EmptyState msg="No properties yet. Add your first rental to load city regulations and generate a compliance checklist." />
            <button onClick={() => setShowAdd(true)} data-testid="empty-add-property" className="mt-3 bg-slate-900 text-white px-4 py-2 rounded-lg text-sm hover:bg-slate-800">Add your first property</button>
          </div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {props.map((p) => (
            <Link to={`/app/properties/${p.id}`} key={p.id} data-testid={`property-card-${p.id}`} className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition">
              <div className="aspect-[16/10] relative bg-slate-100">
                <img src={p.hero_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute top-3 right-3">
                  <ScoreBadge score={p.compliance?.score ?? 0} />
                </div>
              </div>
              <div className="p-4 space-y-2">
                <div className="font-display font-semibold text-slate-900 truncate">{p.name}</div>
                <div className="flex items-center gap-1 text-xs text-slate-500 truncate">
                  <MapPin className="w-3 h-3 shrink-0" /> {p.city_name} · {p.address}
                </div>
                <div className="pt-2 flex items-center gap-3 text-[11px] font-mono">
                  <span className="text-emerald-700">{p.compliance?.compliant ?? 0} ok</span>
                  <span className="text-amber-700">{p.compliance?.warnings ?? 0} warn</span>
                  <span className="text-rose-700">{p.compliance?.non_compliant ?? 0} risk</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {showAdd && <AddPropertyModal groups={groups} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); reload(); }} />}
    </div>
  );
}

function AddPropertyModal({ groups, onClose, onSaved }) {
  const flat = groups.flatMap((g) => g.options.map((o) => ({ ...o, state_name: g.name, state_code: g.code })));
  const [form, setForm] = useState({
    name: "", address: "", city_slug: "nyc",
    property_type: "entire_home", bedrooms: 1, max_occupancy: 2, is_primary_residence: true,
  });
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const selected = flat.find((o) => o.slug === form.city_slug);

  const filtered = q.trim()
    ? flat.filter((o) => (o.name + " " + o.state_name + " " + o.state_code).toLowerCase().includes(q.toLowerCase())).slice(0, 40)
    : [];

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/properties", form);
      toast.success("Property added — checklist ready");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose} data-testid="add-property-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="font-display text-xl font-semibold">Add property</h2>
          <button onClick={onClose} data-testid="close-add-property" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Input label="Property name" value={form.name} onChange={(v) => set("name", v)} testid="prop-name" required />
          <Input label="Full address" value={form.address} onChange={(v) => set("address", v)} testid="prop-address" required />

          <div>
            <span className="text-xs font-mono uppercase tracking-widest text-slate-600">Jurisdiction</span>
            <div className="mt-1.5 rounded-lg border border-slate-300 focus-within:border-sky-500">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200">
                <Search className="w-4 h-4 text-slate-400" />
                <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search city or state (e.g. Denver, Texas)"
                  data-testid="prop-city-search" className="flex-1 outline-none text-sm" />
              </div>
              {q.trim() && filtered.length > 0 && (
                <ul className="max-h-52 overflow-y-auto divide-y divide-slate-100" data-testid="prop-city-options">
                  {filtered.map((o) => (
                    <li key={o.slug}>
                      <button type="button" onClick={() => { set("city_slug", o.slug); setQ(""); }}
                        data-testid={`opt-${o.slug}`}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between gap-2 ${form.city_slug === o.slug ? "bg-sky-50" : ""}`}>
                        <span><span className="font-medium text-slate-800">{o.name}</span> <span className="text-slate-400 text-xs">· {o.state_name}</span></span>
                        <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded ${o.jurisdiction_type === "city" ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-600"}`}>{o.jurisdiction_type}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="px-3 py-2 text-xs text-slate-600 bg-slate-50" data-testid="prop-city-selected">
                Selected: <span className="font-medium text-slate-900">{selected ? `${selected.name}, ${selected.state_name}` : "—"}</span>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Not seeing your city? Pick your state (Statewide) framework.</p>
          </div>

          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest text-slate-600">Type</span>
            <select value={form.property_type} onChange={(e) => set("property_type", e.target.value)} data-testid="prop-type"
              className="mt-1.5 w-full px-4 py-3 rounded-lg border border-slate-300 bg-white outline-none focus:border-sky-500">
              <option value="entire_home">Entire home</option>
              <option value="private_room">Private room</option>
              <option value="shared">Shared</option>
            </select>
          </label>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Bedrooms" type="number" min={0} value={form.bedrooms} onChange={(v) => set("bedrooms", parseInt(v || 0))} testid="prop-bedrooms" />
            <Input label="Max guests" type="number" min={1} value={form.max_occupancy} onChange={(v) => set("max_occupancy", parseInt(v || 1))} testid="prop-max" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.is_primary_residence} onChange={(e) => set("is_primary_residence", e.target.checked)} data-testid="prop-primary" className="w-4 h-4 rounded" />
            This is my primary residence
          </label>
          <button disabled={saving} data-testid="submit-add-property" className="w-full bg-slate-900 hover:bg-slate-800 text-white py-3 rounded-lg font-medium disabled:opacity-50">
            {saving ? "Saving…" : "Add property"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid, type = "text", ...rest }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-widest text-slate-600">{label}</span>
      <input {...rest} type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1.5 w-full px-4 py-3 rounded-lg border border-slate-300 bg-white focus:border-sky-500 outline-none" />
    </label>
  );
}
